
// Missing/invalid timestamps parse to NaN, which makes the subtraction
// comparator non-deterministic. Treat them as epoch 0 so such rows sort to the

import { IMessage } from "@endpoints/types/message.type";

// bottom (oldest) deterministically.
const toTime = (value?: string | Date | null): number => {
  const t = value ? new Date(value).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};


// NOTE: no chatId fallback. chatId is a conversation-level composite
// (jobId_recipientId_senderId), identical for every message in the room — using
// it as an identity would collapse the whole conversation into a single row.
export const identity = (m: IMessage): string =>
  m?.clientMsgId ?? m?.messageId ?? m?._id ?? '';

/**
 * Field-level merge when a placeholder and its server copy collapse onto the
 * same identity. Server fields win, with two local-only guards:
 *  - don't let a server row without an upload status clobber an in-flight local
 *    upload ('uploading' / 'failed');
 *  - keep local image previews until the server actually returns attachments.
 */
const mergeOne = (local: IMessage, server: IMessage): IMessage => ({
  ...local,
  ...server,
  // A delayed send echo/history page must not undo a newer read receipt.
  seen: local.seen === true || server.seen === true,
  uploadStatus: server?.uploadStatus ?? local?.uploadStatus,
  attachments: server?.attachments?.length
    ? server.attachments
    : local?.attachments,
});

const sortNewestFirst = (list: IMessage[]): IMessage[] =>
  [...list].sort((a, b) => toTime(b?.createdAt) - toTime(a?.createdAt));

/**
 * Idempotent upsert of `incoming` messages into `prev`, deduped on {@link identity}
 * and returned newest-first (for the inverted FlatList).
 *
 * `clientMsgId` is the sole correlation key: it's present on the optimistic
 * placeholder and echoed back verbatim by the server on the socket + REST copies,
 * so they collapse onto one identity and the server copy upserts in place
 * (preserving local-only fields). There is intentionally NO `chatId` fallback —
 * the server does not round-trip the client `chatId`, so matching on it would
 * never fire and only give false comfort.
 *
 * Safe to call with `incoming` from either the REST page or a single socket event.
 */
export const mergeMessages = (
  prev: IMessage[],
  incoming: IMessage[],
): IMessage[] => {
  if (!incoming?.length) return prev;

  const byId = new Map<string, IMessage>();
  for (const m of prev ?? []) {
    const id = identity(m);
    if (id) byId.set(id, m);
  }

  for (const m of incoming) {
    const id = identity(m);
    if (!id) continue;
    const existing = byId.get(id);
    byId.set(id, existing ? mergeOne(existing, m) : m);
  }

  return sortNewestFirst([...byId.values()]);
};
